import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useEffect, useState } from "react";
import ButtonComponent from "../../../../common/components/button/button.component";
import LoaderComponent from "../../../../common/components/loader/loader.component";
import NoticeBoxType from "../../../../common/components/notice-box/notice-box-type";
import NoticeBoxComponent from "../../../../common/components/notice-box/notice-box.component";
import orderTranslationsHelper from "../../../../languages/order-translations.helper";
import orderDetailsHistoryEntryFactory from "./common/order-details-history-entry.factory";
import OrderDetailsHistoryEntry from "./common/types/order-details-history-entry";
import OrderDetailsHistoryEntryComponent from "./entry/order-details-history-entry.component";
import useOrderDetailsHistoryUserPermissions from "./common/user-permissions/use-order-details-history-user-permissions";
import useOrderCargoOrderDetailsHandlingLog from "../../../../common/services/order/cargo-order/handling-log/use-order-cargo-order-details-handling-log";
import useAbort from "../../../../common/hooks/use-abort";
import OrderCargoOrderDetailsHandlingLogParams from "../../../../common/services/order/cargo-order/handling-log/order-cargo-order-details-handling-log-params";
import orderDetailsHandlingLogLoadParamsFactory from "./common/types/order-details-handling-log-load-params.factory";
import OrderCargoOrderHistoryParams from "../../../../common/services/order/cargo-order/history/order-cargo-order-history-params";
import useOrderCargoOrderHistory from "../../../../common/services/order/cargo-order/history/use-order-cargo-order-history";

type OrderDetailsHistoryProps = {
  orderUuid: string;
  refetchFlag: boolean;
};

const OrderDetailsHistoryComponent: FC<OrderDetailsHistoryProps> = (props) => {
  const [historyEntries, setHistoryEntries] = useState<
    OrderDetailsHistoryEntry[]
  >([]);

  const handlingLogAbort = useAbort();
  const orderHandlingLog = useOrderCargoOrderDetailsHandlingLog();
  const userPermissions = useOrderDetailsHistoryUserPermissions();
  const translations = orderTranslationsHelper.getDetailsHistoryTranslations();

  const cargoOrderHistory = useOrderCargoOrderHistory();
  const cargoOrderHistoryAbort = useAbort();

  useEffect(() => {
    if (!userPermissions.hasAccessToHandlingLog) return;

    const params: OrderCargoOrderDetailsHandlingLogParams =
      orderDetailsHandlingLogLoadParamsFactory.create(props.orderUuid);

    orderHandlingLog.load(params, handlingLogAbort.signal);
  }, []);

  useEffect(() => {
    if (
      !cargoOrderHistory.isLoading &&
      !orderHandlingLog.isLoading &&
      cargoOrderHistory.data &&
      orderHandlingLog.data
    ) {
      const historyEntries =
        orderDetailsHistoryEntryFactory.createHistoryEntries(
          cargoOrderHistory.data,
          orderHandlingLog.data.data,
        );

      setHistoryEntries(historyEntries);
    }
  }, [
    cargoOrderHistory.isLoading,
    orderHandlingLog.isLoading,
    cargoOrderHistory.data,
    orderHandlingLog.data,
  ]);

  const loadCargoOrderHistory = () => {
    const params: OrderCargoOrderHistoryParams = {
      orderUuid: props.orderUuid,
    };

    cargoOrderHistory.load(params, cargoOrderHistoryAbort.signal);
  };

  useEffect(() => {
    loadCargoOrderHistory();
  }, [props.orderUuid, props.refetchFlag]);

  const onRefreshButtonClick = () => {
    loadCargoOrderHistory();
  };

  const LoaderContent = (
    <div className="order_details_history_loader_wrapper">
      <LoaderComponent type="primary" />
    </div>
  );

  const ErrorContent = (
    <NoticeBoxComponent
      type={NoticeBoxType.DANGER}
      classNames={{ root: "w-100 mt-2" }}
    >
      {translations.failureFetchHistoryNotificationMessageText}
    </NoticeBoxComponent>
  );

  return (
    <div className="order_details_history">
      <div className="order_details_history__heading">
        <div className="order_details_history__heading_text">
          {translations.headingText}
        </div>
        <ButtonComponent
          type="primary"
          onClick={onRefreshButtonClick}
          classNames={{ root: "p-2 order_details_history_refresh_button" }}
          title={translations.refreshButtonTitle}
        >
          <FontAwesomeIcon
            icon={faArrowsRotate}
            size="lg"
            className="order_details_history_refresh_button__icon"
          />
        </ButtonComponent>
      </div>
      {cargoOrderHistory.isLoading ? (
        LoaderContent
      ) : cargoOrderHistory.isError ? (
        ErrorContent
      ) : (
        <ul className="order_details_history_entry_list">
          {historyEntries.map((entry, index) => {
            return (
              <OrderDetailsHistoryEntryComponent key={index} entry={entry} />
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default OrderDetailsHistoryComponent;
